'use client';

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react';
import { workspacesApi, WorkspaceResponse } from '@/api/workspaces';
import { usersApi } from '@/api/users';
import { trackCreateWorkspace } from '@/lib/analytics';
import { useAuth } from './AuthContext';

// Types
interface WorkspaceState {
  workspaces: WorkspaceResponse[];
  selectedWorkspaceId: string | null;
  isLoading: boolean;
}

type WorkspaceAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_WORKSPACES'; payload: WorkspaceResponse[] }
  | { type: 'SET_SELECTED_WORKSPACE'; payload: string | null }
  | { type: 'ADD_WORKSPACE'; payload: WorkspaceResponse }
  | { type: 'UPDATE_WORKSPACE'; payload: WorkspaceResponse }
  | { type: 'REMOVE_WORKSPACE'; payload: string }
  | { type: 'RESET_STATE' };

interface WorkspaceContextValue extends WorkspaceState {
  setSelectedWorkspace: (workspaceId: string | null) => Promise<void>;
  createWorkspace: (name: string) => Promise<WorkspaceResponse>;
  updateWorkspace: (id: string, name: string) => Promise<WorkspaceResponse>;
  deleteWorkspace: (id: string) => Promise<void>;
  loadWorkspaces: () => Promise<void>;
  getCurrentWorkspace: () => WorkspaceResponse | undefined;
}

// Initial state
const initialState: WorkspaceState = {
  workspaces: [],
  selectedWorkspaceId: null,
  isLoading: true,
};

// Reducer
function workspaceReducer(state: WorkspaceState, action: WorkspaceAction): WorkspaceState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_WORKSPACES':
      return {
        ...state,
        workspaces: action.payload,
      };

    case 'SET_SELECTED_WORKSPACE':
      return {
        ...state,
        selectedWorkspaceId: action.payload,
      };

    case 'ADD_WORKSPACE':
      return {
        ...state,
        workspaces: [...state.workspaces, action.payload],
      };

    case 'UPDATE_WORKSPACE':
      return {
        ...state,
        workspaces: state.workspaces.map((w) =>
          w.id === action.payload.id ? action.payload : w
        ),
      };

    case 'REMOVE_WORKSPACE':
      return {
        ...state,
        workspaces: state.workspaces.filter((w) => w.id !== action.payload),
        selectedWorkspaceId:
          state.selectedWorkspaceId === action.payload ? null : state.selectedWorkspaceId,
      };

    case 'RESET_STATE':
      return {
        workspaces: [],
        selectedWorkspaceId: null,
        isLoading: false,
      };

    default:
      return state;
  }
}

// Context
const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

// Provider
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(workspaceReducer, initialState);
  const { user, isAuthenticated, updateUserFromApi } = useAuth();

  // Load workspaces
  const loadWorkspaces = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const workspaces = await workspacesApi.getAll();
      dispatch({ type: 'SET_WORKSPACES', payload: workspaces });

      // Use workspace from user (JWT) if available, otherwise use state
      const currentWorkspaceId = user?.currentWorkspaceId || state.selectedWorkspaceId;
      
      // Validate and set selected workspace
      const selectedWorkspaceExists = currentWorkspaceId && workspaces.some(w => w.id === currentWorkspaceId);
      
      if (currentWorkspaceId && selectedWorkspaceExists) {
        // Use workspace from user (JWT) if it exists
        dispatch({ type: 'SET_SELECTED_WORKSPACE', payload: currentWorkspaceId });
      } else if (!currentWorkspaceId || !selectedWorkspaceExists) {
        // Auto-select first workspace if none selected or selected doesn't exist
        if (workspaces.length > 0) {
          const firstWorkspaceId = workspaces[0].id;
          dispatch({ type: 'SET_SELECTED_WORKSPACE', payload: firstWorkspaceId });
          try {
            const updatedUser = await usersApi.setCurrentWorkspace(firstWorkspaceId);
            updateUserFromApi(updatedUser);
          } catch (error) {
            console.error('Error setting workspace:', error);
          }
        } else {
          // No workspaces available
          dispatch({ type: 'SET_SELECTED_WORKSPACE', payload: null });
        }
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [user?.currentWorkspaceId, state.selectedWorkspaceId, updateUserFromApi]);

  // Set selected workspace
  const setSelectedWorkspace = useCallback(async (workspaceId: string | null) => {
    try {
      const updatedUser = await usersApi.setCurrentWorkspace(workspaceId);
      updateUserFromApi(updatedUser);
      dispatch({ type: 'SET_SELECTED_WORKSPACE', payload: workspaceId });
    } catch (error) {
      console.error('Error setting workspace:', error);
      dispatch({ type: 'SET_SELECTED_WORKSPACE', payload: workspaceId });
    }
  }, [updateUserFromApi]);

  // Create workspace
  const createWorkspace = useCallback(async (name: string): Promise<WorkspaceResponse> => {
    const created = await workspacesApi.create({ name });
    dispatch({ type: 'ADD_WORKSPACE', payload: created });
    // Auto-select newly created workspace and update backend
    await setSelectedWorkspace(created.id);
    trackCreateWorkspace();
    return created;
  }, [setSelectedWorkspace]);

  // Update workspace
  const updateWorkspace = useCallback(
    async (id: string, name: string): Promise<WorkspaceResponse> => {
      const updated = await workspacesApi.update(id, { name });
      dispatch({ type: 'UPDATE_WORKSPACE', payload: updated });
      return updated;
    },
    []
  );

  // Delete workspace
  const deleteWorkspace = useCallback(async (id: string): Promise<void> => {
    await workspacesApi.delete(id);
    dispatch({ type: 'REMOVE_WORKSPACE', payload: id });
    // If deleted workspace was selected, select first available or null
    const remaining = state.workspaces.filter((w) => w.id !== id);
    if (state.selectedWorkspaceId === id) {
      if (remaining.length > 0) {
        dispatch({ type: 'SET_SELECTED_WORKSPACE', payload: remaining[0].id });
      } else {
        dispatch({ type: 'SET_SELECTED_WORKSPACE', payload: null });
      }
    }
  }, [state.workspaces, state.selectedWorkspaceId]);

  // Get current workspace
  const getCurrentWorkspace = useCallback((): WorkspaceResponse | undefined => {
    if (!state.selectedWorkspaceId) return undefined;
    return state.workspaces.find((w) => w.id === state.selectedWorkspaceId);
  }, [state.selectedWorkspaceId, state.workspaces]);

  // Sync workspace from user (JWT) when user changes
  useEffect(() => {
    if (isAuthenticated && user?.currentWorkspaceId) {
      // Sync workspace from JWT/user
      if (state.selectedWorkspaceId !== user.currentWorkspaceId) {
        dispatch({ type: 'SET_SELECTED_WORKSPACE', payload: user.currentWorkspaceId });
      }
    }
  }, [isAuthenticated, user?.currentWorkspaceId, state.selectedWorkspaceId]);

  // Track user ID to avoid reloading when only credits change
  const userIdRef = useRef<string | null>(null);
  const hasLoadedWorkspacesRef = useRef(false);
  
  // Load workspaces when user is authenticated (but not when only credits change)
  useEffect(() => {
    const currentUserId = user?.id || null;
    const userIdChanged = userIdRef.current !== currentUserId;
    
    if (userIdChanged) {
      userIdRef.current = currentUserId;
      hasLoadedWorkspacesRef.current = false; // Reset flag when user changes
    }
    
    if (isAuthenticated && user) {
      // Load workspaces if user ID changed OR if this is the first load
      if (userIdChanged || !hasLoadedWorkspacesRef.current) {
        hasLoadedWorkspacesRef.current = true;
        loadWorkspaces();
      }
    } else {
      // Reset state when user logs out
      dispatch({ type: 'RESET_STATE' });
      userIdRef.current = null;
      hasLoadedWorkspacesRef.current = false;
    }
  }, [isAuthenticated, user?.id, loadWorkspaces]);

  const value: WorkspaceContextValue = {
    ...state,
    setSelectedWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    loadWorkspaces,
    getCurrentWorkspace,
  };

  return (
    <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
  );
}

// Hook
export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
